import { apiFetch } from "@/lib/api";

export type PottingSendFlow = "first_pass" | "rework";

export interface PottingSendCartonSummary {
  flow?: PottingSendFlow;
  cartonNumber: number;
  assignedBy?: string;
  laborSku: string;
  productLabel: string;
  sealed: boolean;
  sealedAt?: string | null;
  labelPrintedAt?: string | null;
  rowCount: number;
}

export interface PottingSendDraftOverview {
  cartons: PottingSendCartonSummary[];
  unsealedCartonNumbers: number[];
  openDraftRowCount: number;
  rowsMissingCarton: number;
  activePottingSendPurchaseOrderMvNumber: string | null;
}

export interface ScsPottingSendOverview {
  waitingForPoCount: number;
  firstPass: PottingSendDraftOverview;
  rework: PottingSendDraftOverview;
  cartons: PottingSendCartonSummary[];
  badgeCounts: {
    firstPassInDraft: number;
    firstPassCompleteAtCalTest: number;
    reworkInDraft: number;
    reworkEligibleForRework: number;
  };
}

export interface ScsPottingDraftTableRow {
  id: number;
  sendFlow: PottingSendFlow;
  rowKind: string;
  serial: string | null;
  cartonNumber: number | null;
  skuCoreFeature: string | null;
  finishedSku: string | null;
  laborSku: string | null;
  productLabel: string | null;
}

export interface ScsPottingDraftTableOverview {
  rows: ScsPottingDraftTableRow[];
}

export interface CreateFirstPassPottingPoResult {
  ok: boolean;
  public_purchase_order_id: number;
  mv_purchase_order_id: number | null;
  mv_purchase_order_number: string | null;
  linked_potting_rows: number;
  station_serial_updates: number;
}

export interface CreateReworkPottingPoResult {
  ok: boolean;
  public_purchase_order_id: number;
  mv_purchase_order_id: number | null;
  mv_purchase_order_number: string | null;
  linked_rework_rows: number;
  station_serial_updates: number;
}

export interface InternalPottingPendingRow {
  id: number;
  serial: string;
  serialKind: "device" | "clamp";
  sentAt: string | null;
  sentBy: string | null;
  finishedSku: string | null;
}

export interface InternalPottingPendingOverview {
  rows: InternalPottingPendingRow[];
}

export const POTTING_SEND_INTERNAL_PENDING_KEY = [
  "manufacturing",
  "potting-send",
  "first-pass",
  "internal-pending",
] as const;
export const POTTING_SEND_REWORK_INTERNAL_PENDING_KEY = [
  "manufacturing",
  "potting-send",
  "rework",
  "internal-pending",
] as const;
export const POTTING_SEND_FIRST_PASS_DRAFT_KEY = ["manufacturing", "potting-send", "first-pass", "draft"] as const;
export const POTTING_SEND_REWORK_DRAFT_KEY = ["manufacturing", "potting-send", "rework", "draft"] as const;
export const POTTING_SEND_SCS_DRAFT_KEY = ["manufacturing", "potting-send", "scs", "draft"] as const;
export const POTTING_SEND_SCS_DRAFT_ROWS_KEY = ["manufacturing", "potting-send", "scs", "draft-rows"] as const;
export const POTTING_SEND_OPERATORS_KEY = ["manufacturing", "potting-send", "operators"] as const;

export interface PottingSendOperatorOption {
  email: string;
  fullName: string | null;
}

export interface ReassignPottingSendCartonResult {
  ok: boolean;
  cartonNumber: number;
  previousOperatorEmail: string;
  newOperatorEmail: string;
}

export function fetchPottingSendFirstPassDraft(): Promise<PottingSendDraftOverview> {
  return apiFetch<PottingSendDraftOverview>("/api/manufacturing/potting-send/first-pass/draft", {
    cache: "no-store",
  });
}

export function fetchPottingSendReworkDraft(): Promise<PottingSendDraftOverview> {
  return apiFetch<PottingSendDraftOverview>("/api/manufacturing/potting-send/rework/draft", {
    cache: "no-store",
  });
}

export function fetchScsPottingSendDraft(options?: {
  refresh?: boolean;
  background?: boolean;
}): Promise<ScsPottingSendOverview> {
  const query = new URLSearchParams();
  if (options?.refresh === true) {
    query.set("refresh", "1");
  }
  if (options?.background === true) {
    query.set("background", "1");
  }
  const suffix = query.size > 0 ? `?${query.toString()}` : "";
  return apiFetch<ScsPottingSendOverview>(`/api/manufacturing/potting-send/scs/draft${suffix}`, {
    cache: "no-store",
  });
}

export function fetchScsPottingDraftRows(options?: {
  refresh?: boolean;
  background?: boolean;
}): Promise<ScsPottingDraftTableOverview> {
  const query = new URLSearchParams();
  if (options?.refresh === true) {
    query.set("refresh", "1");
  }
  if (options?.background === true) {
    query.set("background", "1");
  }
  const suffix = query.size > 0 ? `?${query.toString()}` : "";
  return apiFetch<ScsPottingDraftTableOverview>(`/api/manufacturing/potting-send/scs/draft-rows${suffix}`, {
    cache: "no-store",
  });
}

export function fetchInternalPottingPending(): Promise<InternalPottingPendingOverview> {
  return apiFetch<InternalPottingPendingOverview>("/api/manufacturing/potting-send/first-pass/internal-pending", {
    cache: "no-store",
  });
}

export function createPottingSendFirstPassPo(): Promise<CreateFirstPassPottingPoResult> {
  return apiFetch<CreateFirstPassPottingPoResult>("/api/manufacturing/potting-send/first-pass/potting-po", {
    method: "POST",
    body: {},
  });
}

export interface InternalPottingSendResult {
  ok: true;
  pottingId: number;
  serial: string;
  serialFciId: number;
  stationId: number;
  createdPottingRow: boolean;
}

export function sendInternalPotting(serial: string): Promise<InternalPottingSendResult> {
  return apiFetch<InternalPottingSendResult>("/api/manufacturing/potting-send/first-pass/internal-send", {
    method: "POST",
    body: { serial: serial.trim() },
  });
}

export interface InternalPottingReworkPendingRow {
  id: number;
  serial: string;
  sentAt: string;
  sentBy: string | null;
  finishedSku: string | null;
  incidentId: number;
  issue: string | null;
}

export interface InternalPottingReworkPendingOverview {
  rows: InternalPottingReworkPendingRow[];
}

export function fetchInternalPottingReworkPending(): Promise<InternalPottingReworkPendingOverview> {
  return apiFetch<InternalPottingReworkPendingOverview>(
    "/api/manufacturing/potting-send/rework/internal-pending",
    { cache: "no-store" },
  );
}

export interface InternalPottingReworkSendResult {
  ok: true;
  pottingReworkId: number;
  serial: string;
  serialFciId: number;
  stationId: number;
}

export function sendInternalPottingRework(serial: string): Promise<InternalPottingReworkSendResult> {
  return apiFetch<InternalPottingReworkSendResult>("/api/manufacturing/potting-send/rework/internal-send", {
    method: "POST",
    body: { serial: serial.trim() },
  });
}

export function createPottingSendReworkPo(): Promise<CreateReworkPottingPoResult> {
  return apiFetch<CreateReworkPottingPoResult>("/api/manufacturing/potting-send/rework/create-po", {
    method: "POST",
    body: {},
  });
}

export function printPottingSendFirstPassCarton(
  cartonNumber: number,
  printer: string,
): Promise<{ printed: boolean; lineCount: number }> {
  return apiFetch(`/api/manufacturing/potting-send/first-pass/cartons/${cartonNumber}/print`, {
    method: "POST",
    body: { printer },
  });
}

export function printPottingSendReworkCarton(
  cartonNumber: number,
  printer: string,
): Promise<{ printed: boolean; lineCount: number }> {
  return apiFetch(`/api/manufacturing/potting-send/rework/cartons/${cartonNumber}/print`, {
    method: "POST",
    body: { printer },
  });
}

export function fetchPottingSendOperators(): Promise<{ operators: PottingSendOperatorOption[] }> {
  return apiFetch<{ operators: PottingSendOperatorOption[] }>("/api/manufacturing/potting-send/operators", {
    cache: "no-store",
  });
}

export function reassignPottingSendFirstPassCarton(
  cartonNumber: number,
  operatorEmail: string,
): Promise<ReassignPottingSendCartonResult> {
  return apiFetch<ReassignPottingSendCartonResult>(
    `/api/manufacturing/potting-send/first-pass/cartons/${cartonNumber}/reassign`,
    {
      method: "POST",
      body: { operatorEmail },
    },
  );
}

export function reassignPottingSendReworkCarton(
  cartonNumber: number,
  operatorEmail: string,
): Promise<ReassignPottingSendCartonResult> {
  return apiFetch<ReassignPottingSendCartonResult>(
    `/api/manufacturing/potting-send/rework/cartons/${cartonNumber}/reassign`,
    {
      method: "POST",
      body: { operatorEmail },
    },
  );
}

export function removeEmptyPottingSendFirstPassCarton(
  cartonNumber: number,
): Promise<{ ok: boolean; cartonNumber: number }> {
  return apiFetch(`/api/manufacturing/potting-send/first-pass/cartons/${cartonNumber}`, {
    method: "DELETE",
  });
}

export function removeEmptyPottingSendReworkCarton(
  cartonNumber: number,
): Promise<{ ok: boolean; cartonNumber: number }> {
  return apiFetch(`/api/manufacturing/potting-send/rework/cartons/${cartonNumber}`, {
    method: "DELETE",
  });
}

export function sealPottingSendFirstPassCarton(
  cartonNumber: number,
): Promise<{ ok: boolean; cartonNumber: number; sealed: boolean }> {
  return apiFetch(`/api/manufacturing/potting-send/first-pass/cartons/${cartonNumber}/seal`, {
    method: "POST",
    body: {},
  });
}

export function sealPottingSendReworkCarton(
  cartonNumber: number,
): Promise<{ ok: boolean; cartonNumber: number; sealed: boolean }> {
  return apiFetch(`/api/manufacturing/potting-send/rework/cartons/${cartonNumber}/seal`, {
    method: "POST",
    body: {},
  });
}

export function unsealPottingSendFirstPassCarton(
  cartonNumber: number,
): Promise<{ ok: boolean; cartonNumber: number; unsealed: boolean }> {
  return apiFetch(`/api/manufacturing/potting-send/first-pass/cartons/${cartonNumber}/unseal`, {
    method: "POST",
    body: {},
  });
}

export function unsealPottingSendReworkCarton(
  cartonNumber: number,
): Promise<{ ok: boolean; cartonNumber: number; unsealed: boolean }> {
  return apiFetch(`/api/manufacturing/potting-send/rework/cartons/${cartonNumber}/unseal`, {
    method: "POST",
    body: {},
  });
}

export interface MoveVendorPottingCartonToInternalResult {
  ok: true;
  sourceCartonNumber: number;
  movedRowCount: number;
  laborSku: string;
  productLabel: string;
  stationId: number;
}

export function moveVendorPottingCartonToInternal(
  cartonNumber: number,
): Promise<MoveVendorPottingCartonToInternalResult> {
  return apiFetch<MoveVendorPottingCartonToInternalResult>(
    `/api/manufacturing/potting-send/first-pass/cartons/${cartonNumber}/move-to-internal`,
    {
      method: "POST",
      body: {},
    },
  );
}
